import React from 'react';
import { InjectedIntl } from 'react-intl';
import { History, LocationState } from 'history';
import { PUBLIC_PATH, ROUTE } from 'utils/constants';
import getLogger from 'utils/getLogger';
import { sendStepLoadedEvent } from 'services/analytics';
import { EVENT_IDS } from 'services/analytics/constants';
import { AppErrorData, ConfigData } from 'types/interfaces';
import {
  clearAuthenticationWith,
  isAuthenticatedWith,
  OKTA_AUTHENT_TYPE,
  signinSSO,
  signupSSO,
  validateApplication,
} from 'services/loginManager';
import { checkAccessTokenValidity, persistUnifiedLogin } from 'services/ssoRedirectService/oktaService';
import { getBncId, loadItem, saveItem } from 'utils';
import CONFIG from './config';
import { getErrorProps } from 'components/errorManager/utils';
import Modal from 'components/Modal/Modal';
import ExistedApplication from '../CreateUserProfile/components/ExistedApplication';

import '../CreateUserProfile/styles.scss';

export type WelcomeProps = {
  intl: InjectedIntl;
  history: History<LocationState>;
  config: ConfigData;
};

export type WelcomeState = {
  accessToken: null | OktaAccessToken;
  applicationInProgress: boolean;
  isTechnicalError: boolean;
  errorData: null | AppErrorData;
};

const logger = getLogger('Welcome');
const clearHistoryHash = (history: History<LocationState>) => {
  history.replace({
    pathname: history.location.pathname || window.location.pathname,
    search: history.location.search || window.location.search,
    hash: '',
  });
};

export const redirectToLogin = (history: History<LocationState>) => {
  history.push({
    pathname: ROUTE.CLIENT_REDIRECT,
    hash: '',
  });
};

export default class Welcome extends React.Component<WelcomeProps, WelcomeState> {
  constructor(props) {
    super(props);
    this.state = {
      accessToken: null,
      applicationInProgress: false,
      isTechnicalError: false,
      errorData: null,
    };
  }

  componentDidMount() {
    window.addEventListener('pageshow', this.handlePageShow);
    this.bootstrapWelcomeFlow();
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  goToAuthenticationOperation(isSignIn = false): Promise<void> {
    const authenticationOperation = isSignIn ? signinSSO : signupSSO;

    return authenticationOperation()
      .then(() => {
        saveItem('newRequest', 'true');
        logger.log(`[AUTH] redirecting to STEPPER_MANAGER page`);
        window.location.assign(`${process.env.PUBLIC_URL}${ROUTE.STEPPER_MANAGER}`);
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCKS !== 'false') {
          // In local dev with mocks, the backend rejects mock tokens. Proceed to stepper anyway.
          logger.log(`[AUTH][DEV] Backend rejected auth, proceeding to STEPPER_MANAGER`);
          saveItem('newRequest', 'true');
          window.location.assign(`${PUBLIC_PATH}${ROUTE.STEPPER_MANAGER}`);
          return;
        }
        logger.error(`[AUTH] ERROR redirecting, redirecting to CLIENT_REDIRECT page`, err);
        redirectToLogin(this.props.history);
      });
  }

  handleRedirect(): void {
    const { history, intl } = this.props;

    if (isAuthenticatedWith(OKTA_AUTHENT_TYPE)) {
      checkAccessTokenValidity(intl.locale)
        .then((accessToken) => {
          if (!accessToken) {
            clearAuthenticationWith();
            redirectToLogin(history);
            return;
          }

          if (loadItem('newRequest') === 'true') {
            return validateApplication()
              .then(() => {
                this.setState({ applicationInProgress: true, accessToken });
              })
              .catch((err) => {
                if (err.status === 400) {
                  this.goToAuthenticationOperation();
                } else {
                  this.setState({ isTechnicalError: true, errorData: getErrorProps(intl, err.status) });
                }
              });
          } else if (loadItem('newRequest') === 'false') {
            this.goToAuthenticationOperation();
          } else {
            this.goToAuthenticationOperation(true);
          }
        })
        .catch((err) => {
          logger.error(`[AUTH] ERROR getting the access token before signin, redirecting to CLIENT_REDIRECT page`, err);
          clearAuthenticationWith();
          redirectToLogin(history);
        });
    } else if (process.env.NODE_ENV !== 'development' || process.env.REACT_APP_USE_MOCKS === 'false') {
      // BAO login or real backend mode — redirect to proper auth
      history.push({
        pathname: ROUTE.CLIENT_REDIRECT,
      });
    } else {
      // Local dev with mocks — skip Okta, show application choice directly
      this.setState({ applicationInProgress: true, accessToken: { fake: true } as any });
    }
  }

  render() {
    const { accessToken, applicationInProgress, isTechnicalError, errorData } = this.state;
    const showApplicationChoice =
      (accessToken && applicationInProgress) ||
      (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCKS !== 'false');

    return (
      <div className="create-user-profile-wrapper">
        {showApplicationChoice && <ExistedApplication intl={this.props.intl} history={this.props.history} />}
        {isTechnicalError && errorData && <Modal {...errorData} name={errorData.title} text={errorData.text} />}
      </div>
    );
  }

  private readonly handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      this.bootstrapWelcomeFlow();
    }
  };

  private bootstrapWelcomeFlow(): void {
    const { history, intl } = this.props;

    const bncId = getBncId();
    sendStepLoadedEvent(CONFIG.ID, EVENT_IDS.FS, '', {
      user: {
        bncId,
      },
    });

    if (history.location.hash.includes('access_token')) {
      persistUnifiedLogin(intl.locale)
        .then(() => {
          this.handleRedirect();
        })
        .catch(() => {
          redirectToLogin(history);
        })
        .finally(() => {
          clearHistoryHash(history);
        });
      return;
    }

    this.handleRedirect();
  }
}
